import { TOKEN_NAME } from "@/constant";
import { appQueryClient } from "@/query/client";

const AUTH_SESSION_MARKER = "gymgoerAdminAuthenticated";
export type AuthMode = "legacy" | "bearer" | "cookie";

let protectedRequestController = new AbortController();

const publicRequestPaths = [
  "/v1/public/",
  "/v1/admin/admin-login",
  "/v1/account/phoneNumber-check",
  "/v1/account/login-mobile",
  "/v1/account/auth/sessions/google",
  "/v1/account/account-remove"
];

// Keep the old protocol by default; bearer or HttpOnly Cookie can be switched in stages through VITE_AUTH_MODE when the backend is ready.
export const AUTH_MODE: AuthMode =
  import.meta.env.VITE_AUTH_MODE === "cookie" || import.meta.env.VITE_AUTH_MODE === "bearer"
    ? import.meta.env.VITE_AUTH_MODE
    : "legacy";

const clearClientAuthStorage = () => {
  localStorage.removeItem(TOKEN_NAME);
  localStorage.removeItem(AUTH_SESSION_MARKER);
};

// Clear historical tokens immediately when cookie mode is started to prevent localStorage credentials left over from old versions from continuing to be visible.
if (AUTH_MODE === "cookie") clearClientAuthStorage();

export const getAccessToken = () => localStorage.getItem(TOKEN_NAME);

export const isPublicRequest = (url = "") => publicRequestPaths.some(path => url.startsWith(path));

// After the authentication expires, cancel all protected requests at once by sharing the AbortSignal and prevent new protected requests from being issued.
export const getProtectedRequestSignal = (url = "") =>
  isPublicRequest(url) ? undefined : protectedRequestController.signal;

// HttpOnly Cookie cannot be read synchronously by the front end. The Cookie mode first releases the business route and exits uniformly by the authentication response of any API.
export const hasAuthSession = () => (AUTH_MODE === "cookie" ? true : Boolean(getAccessToken()));

export const persistAuthSession = (token?: string) => {
  if (protectedRequestController.signal.aborted) protectedRequestController = new AbortController();
  if (AUTH_MODE === "cookie") {
    clearClientAuthStorage();
    return;
  }
  if (token) localStorage.setItem(TOKEN_NAME, token);
};

export const clearAuthSession = () => {
  clearClientAuthStorage();
  protectedRequestController.abort();
  appQueryClient.clear();
};
